import React, { useState, useEffect, useRef } from 'react';
import { WillSearch, WillSearchRef } from '../components/WillSearch';
import { useWillSearch } from '../hooks/useWillSearch';
import { Search as SearchIcon, X, TrendingUp, Grid, Heart, MessageCircle, Trophy } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { authenticatedPost } from '../utils/apiUtils';
import { getAccessToken } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import OfferFormModal from '../components/OfferFormModal';

// Import Will interface to ensure type compatibility
import { Will } from '../services/api';

// Utility function for rendering HTML content safely
const renderHTML = (html: string) => {
  return { __html: html };
};

const Search: React.FC = () => {
  const { searchTerm, results, isLoading, setSearchTerm, loadMore } = useWillSearch();
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [trendingSearches, setTrendingSearches] = useState<string[]>([
    'Remote work', 'Website design', 'Logo design', 'Content writing', 'App development', 
    'Video editing', 'Translation', 'Photography', 'Digital marketing', 'Data entry'
  ]);
  const [likingState, setLikingState] = useState<Record<string, boolean>>({});
  const [likedItems, setLikedItems] = useState<Record<string, boolean>>({});
  const [openOfferModalId, setOpenOfferModalId] = useState<string | null>(null);
  
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  // Create a ref for the WillSearch component with correct type
  const willSearchRef = useRef<WillSearchRef>(null);

  // Load search history from localStorage on component mount
  useEffect(() => {
    const savedHistory = localStorage.getItem('searchHistory');
    if (savedHistory) {
      setSearchHistory(JSON.parse(savedHistory));
    }
  }, []);

  // Initialize liked state from results
  useEffect(() => {
    if (results.length > 0) {
      const newLikedState: Record<string, boolean> = {};
      results.forEach(will => {
        newLikedState[will.id] = will.liked || false;
      });
      setLikedItems(newLikedState);
    }
  }, [results]);

  // Save search term to history when search is performed
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    if (term.trim().length > 0) {
      const newHistory = [
        term,
        ...searchHistory.filter(item => item !== term)
      ].slice(0, 10); // Keep only 10 most recent searches
      
      setSearchHistory(newHistory);
      localStorage.setItem('searchHistory', JSON.stringify(newHistory));
    }
  };

  // Handle trending search click
  const handleTrendingClick = (term: string) => {
    // Update the search input field with the term
    if (willSearchRef.current) {
      willSearchRef.current.setInputValue(term);
    }
    
    // Perform the search
    handleSearch(term);
  };

  // Clear a specific search history item
  const removeHistoryItem = (term: string) => {
    const newHistory = searchHistory.filter(item => item !== term);
    setSearchHistory(newHistory);
    localStorage.setItem('searchHistory', JSON.stringify(newHistory));
  };

  // Clear all search history
  const clearHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem('searchHistory');
  };

  // Handle like button click
  const handleLikeToggle = async (e: React.MouseEvent, willId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated || !getAccessToken()) {
      toast.error("Please sign in to like this want");
      navigate('/signin');
      return;
    }
    
    if (likingState[willId]) return; // Prevent multiple clicks
    
    try {
      setLikingState(prev => ({ ...prev, [willId]: true }));
      
      const response = await authenticatedPost(
        `${import.meta.env.VITE_API_URL}/wills/${willId}/like`,
        {}
      );
      
      if (response.ok) {
        // Toggle the like state locally
        setLikedItems(prev => ({ 
          ...prev, 
          [willId]: !prev[willId] 
        }));
      } else {
        throw new Error("Failed to toggle like");
      }
    } catch (error) {
      console.error('Failed to toggle like:', error);
      toast.error("Failed to toggle like");
    } finally {
      setLikingState(prev => ({ ...prev, [willId]: false }));
    }
  };
  
  // Handle comment button click
  const handleCommentClick = (e: React.MouseEvent, willId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error("Please sign in to comment");
      navigate('/signin');
      return;
    }
    
    navigate(`/will/${willId}`, { state: { toggleComments: true } });
  };
  
  // Handle make offer button click
  const handleMakeOfferClick = (e: React.MouseEvent, willId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error("Please sign in to make an offer");
      navigate('/signin');
      return;
    }
    
    // Toggle the offer modal
    setOpenOfferModalId(prevId => prevId === willId ? null : willId);
  };

  // Update just the font sizes and implement the InstagramWillCard time display logic
  // Replace the formatDueDate function with formatFulfillmentTime
  const formatFulfillmentTime = (dateString?: string): React.ReactNode => {
    if (!dateString) return null;
    
    const dueDate = new Date(dateString);
    const now = new Date();
    
    // Calculate the difference in days
    const diffInTime = dueDate.getTime() - now.getTime();
    const diffInDays = Math.ceil(diffInTime / (1000 * 3600 * 24));
    
    if (diffInDays < 0) {
      // Minimalist past date
      return (
        <span className="text-red-500 text-xs">
          {dueDate.toLocaleDateString('en-US', { 
            day: 'numeric', 
            month: 'short'
          })}
        </span>
      );
    }
    
    if (diffInDays === 0) {
      // Due today, let's calculate in how many hours
      const diffInHours = Math.ceil(diffInTime / (1000 * 3600));
      if (diffInHours <= 0) {
        // Show "Today" instead of "Due today"
        return <span className="text-red-500 text-xs">Today</span>;
      }
      return (
        <span className="text-red-500 text-xs">
          in {diffInHours}h
        </span>
      );
    }
    
    if (diffInDays === 1) {
      return <span className="text-orange-500 text-xs">Tomorrow</span>;
    }
    
    if (diffInDays <= 3) {
      return <span className="text-orange-500 text-xs">in {diffInDays}d</span>;
    }
    
    if (diffInDays <= 7) {
      return <span className="text-yellow-600 text-xs">in {diffInDays}d</span>;
    }
    
    // For more than a week, show just the date
    return (
      <span className="text-gray-600 text-xs">
        {dueDate.toLocaleDateString('en-US', { 
          day: 'numeric', 
          month: 'short'
        })}
      </span>
    );
  };

  // Update the renderWillCard function
  const renderWillCard = (will: Will) => {
    const hasMedia = will.media && will.media.length > 0;
    const mediaUrl = hasMedia ? will.media[0].url : null;
    const isLiked = likedItems[will.id] || will.liked || false;
    
    return (
      <div key={will.id} className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all h-full flex flex-col">
        <Link to={`/will/${will.id}`} className="block flex-grow">
          {/* Image or Placeholder */}
          <div className="aspect-square relative bg-gray-100 overflow-hidden">
            {mediaUrl ? (
              <img 
                src={mediaUrl} 
                alt={will.title}
                className="w-full h-full object-cover transition-transform hover:scale-105 duration-300"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-indigo-100">
                <div 
                  className="text-gray-700 font-medium overflow-hidden overflow-ellipsis line-clamp-4"
                  dangerouslySetInnerHTML={renderHTML(will.description || will.title)}
                />
              </div>
            )}
            
            {/* Price Badge (if applicable) */}
            {will.budget && (
              <div className="absolute bottom-2 left-2 bg-yellow-200 text-black px-2 py-1 rounded-md text-sm font-bold">
                {will.currency === 'USD' ? '$' : will.currency === 'EUR' ? '€' : '₪'}{will.budget}
              </div>
            )}
          </div>
          
          {/* Content */}
          <div className="p-4">
            <h3 className="font-bold text-lg mb-2 line-clamp-1" title={will.title}>
              {will.title}
            </h3>
            <div className="text-base text-gray-700 line-clamp-2 mb-3" dangerouslySetInnerHTML={renderHTML(will.description)} />
            
            <div className="flex justify-between items-center">
              <div className="flex items-center text-sm text-gray-500">
                <span className="font-medium">{will.wanterName || 'Anonymous'}</span>
                {will.location && (
                  <>
                    <span className="mx-1">•</span>
                    <span>📍 {will.location}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </Link>
        
        {/* Actions Footer */}
        <div className="px-4 py-3 border-t border-gray-100 flex justify-between mt-auto">
          <div className="flex items-center space-x-5">
            <button 
              onClick={(e) => handleLikeToggle(e, will.id)}
              disabled={likingState[will.id]}
              className="text-gray-500 hover:text-red-500 transition-colors"
            >
              <Heart size={18} className={isLiked ? 'fill-red-500 text-red-500' : ''} />
            </button>
            <button 
              onClick={(e) => handleCommentClick(e, will.id)}
              className="text-gray-500 hover:text-blue-500 transition-colors"
            >
              <MessageCircle size={18} />
            </button>
            <button 
              onClick={(e) => handleMakeOfferClick(e, will.id)}
              className="text-gray-500 hover:text-yellow-500 transition-colors"
            >
              <Trophy size={18} />
            </button>
          </div>
          
          {will.fulfillmentTime && (
            <div className="flex items-center">
              <div className="flex items-center gap-1 text-xs">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                {formatFulfillmentTime(will.fulfillmentTime)}
              </div>
            </div>
          )}
        </div>
        
        {/* OfferFormModal */}
        <OfferFormModal
          isOpen={openOfferModalId === will.id}
          onClose={() => setOpenOfferModalId(null)}
          willId={will.id}
          willTitle={will.title}
          willMedia={will.media || []}
        />
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <h1 className="text-2xl font-bold mb-6">Search</h1>
      
      <WillSearch 
        className="mb-4" 
        onSearch={handleSearch} 
        ref={willSearchRef}
      />
      
      {/* Trending Searches as Minimalist Tags */}
      <div className="mb-8">
        <div className="flex items-center text-gray-500 mb-2">
          <TrendingUp size={14} className="mr-1.5" />
          <span className="text-xs uppercase tracking-wide">Trending</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {trendingSearches.map((term, index) => (
            <button
              key={index}
              onClick={() => handleTrendingClick(term)}
              className="text-sm border border-gray-300 px-3 py-1 rounded-md hover:bg-gray-50 transition-colors"
            >
              {term}
            </button>
          ))}
        </div>
      </div>
      
      {!searchTerm && (
        <>
          {/* Search History Section */}
          {searchHistory.length > 0 && (
            <div className="mb-8">
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-lg font-medium">Recent Searches</h2>
                <button 
                  className="text-sm text-blue-500 hover:underline"
                  onClick={clearHistory}
                >
                  Clear All
                </button>
              </div>
              <div className="space-y-2">
                {searchHistory.map((term, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg hover:bg-gray-100 transition-colors">
                    <button 
                      className="flex items-center flex-grow text-left"
                      onClick={() => handleTrendingClick(term)}
                    >
                      <SearchIcon size={16} className="mr-3 text-gray-500" />
                      <span>{term}</span>
                    </button>
                    <button 
                      className="text-gray-400 hover:text-gray-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeHistoryItem(term);
                      }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Search Results */}
      {searchTerm && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium">Results for "{searchTerm}"</h2>
            <div className="flex items-center text-sm text-gray-500">
              <Grid size={14} className="mr-1.5" />
              <span>{results.length} results</span>
            </div>
          </div>
          
          {isLoading ? (
            <div className="flex justify-center my-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : results.length > 0 ? (
            <>
              {/* Grid Layout for Search Results */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {results.map((will) => renderWillCard(will))}
              </div>
              
              {results.length >= 10 && (
                <div className="mt-8">
                  <button 
                    className="w-full py-3 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                    onClick={loadMore}
                  >
                    Load More
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No results found. Try a different search term.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Search; 